import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Candidacy, CandidacyOffice } from "@shared/types";
import { candidacyPath } from "@shared/share-links";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { useNation } from "../lib/useNation";
import { Page, Card, Spinner, ErrorBox } from "../components/ui";
import { ClaimBadge, OriginBadge } from "../components/StatusBadge";
import { flag } from "../lib/flag";
import { useT } from "../i18n";

// Top-level candidate directory + run-for-office entry point.
export default function Candidates() {
  const t = useT();
  const { nation } = useNation();
  const [mineOnly, setMineOnly] = useState(false);
  const country = mineOnly && nation ? nation.id : null;
  const { data, loading, error } = useAsync(() => api.candidates(country), [country]);

  return (
    <Page>
      <h1 className="text-xl font-semibold my-3">🎙 {t("candidates.title")}</h1>
      <p className="text-muted text-sm mb-4">{t("candidates.blurb")}</p>

      <RunForOffice />

      {nation && (
        <div className="flex gap-2 my-4">
          <Chip active={!mineOnly} onClick={() => setMineOnly(false)}>{t("candidates.allNations")}</Chip>
          <Chip active={mineOnly} onClick={() => setMineOnly(true)}>{flag(nation.code)} {nation.name}</Chip>
        </div>
      )}

      {loading && <Spinner label={t("race.loadingCandidates")} />}
      {error && <ErrorBox msg={error} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        {(data ?? []).map((cd) => (
          <CandidateCard key={cd.id} cd={cd} />
        ))}
      </div>
      {!loading && !error && (data?.length ?? 0) === 0 && (
        <p className="text-muted text-sm">{t("candidates.empty")}</p>
      )}
    </Page>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-sm px-3 py-1 rounded-full border transition ${active ? "border-accent text-accent bg-accent/10" : "border-border text-muted hover:text-accent"}`}
    >
      {children}
    </button>
  );
}

function CandidateCard({ cd }: { cd: Candidacy }) {
  const t = useT();
  return (
    <Link to={candidacyPath(cd.username, cd.id)}>
      <Card className="cursor-pointer hover:border-accent transition h-full">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold truncate">{cd.username}</span>
          <span className="flex items-center gap-1.5 shrink-0">
            <ClaimBadge claimStatus={cd.claimStatus} />
            <OriginBadge origin={cd.origin} />
          </span>
        </div>
        <div className="text-xs text-muted capitalize mt-0.5">{cd.party ?? t("common.independent")} · {cd.office === "president" ? t("office.president") : t("office.congress")}</div>
        {cd.intro && <p className="text-sm text-muted mt-2 line-clamp-2">{cd.intro}</p>}
        <div className="text-xs text-muted mt-2">
          {t("home.measuresCount", { n: cd.measures.length })}
        </div>
      </Card>
    </Link>
  );
}

/** Onboarding entry: pick an office and head to that race's declare form (which
 * already collects username/party/pitch/measures and then verifies ownership). */
function RunForOffice() {
  const t = useT();
  const { nation } = useNation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold">
        🏛 {t("candidates.run")}
      </button>
    );
  }

  if (!nation) {
    return (
      <Card>
        <p className="text-sm">{t("candidates.pickFirst")}</p>
        <button onClick={() => setOpen(false)} className="text-muted text-sm mt-2">{t("common.cancel")}</button>
      </Card>
    );
  }

  const go = (office: CandidacyOffice) => nav(`/c/${nation.id}/race/${office}`);
  return (
    <Card>
      <h2 className="font-semibold mb-1">{t("candidates.runIn", { nation: nation.name })} {flag(nation.code)}</h2>
      <p className="text-sm text-muted mb-3">{t("candidates.runBlurb")}</p>
      <div className="flex gap-2">
        <button onClick={() => go("president")} className="border border-border rounded-lg px-4 py-2 text-sm hover:border-accent">{t("office.presidentTitle")}</button>
        <button onClick={() => go("congress")} className="border border-border rounded-lg px-4 py-2 text-sm hover:border-accent">{t("office.congressTitle")}</button>
        <button onClick={() => setOpen(false)} className="text-muted text-sm px-3">{t("common.cancel")}</button>
      </div>
    </Card>
  );
}
